import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, User, ChefHat, Truck, Package, Edit, Trash2, X, ChevronLeft, UserCheck, UserX, Key, Copy, CheckCircle, Clock, Eye, ArrowRightLeft } from 'lucide-react'
import { useStore } from '../../../contexts/StoreContext'
import { api } from '../../../lib/api'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'
import { showOfflineToast } from '../../../lib/toastManager'
import { Store } from '../../../types'

interface Employee {
  id: string
  name: string
  email: string
  phone: string
  type: 'store_admin' | 'dispatcher' | 'chef' | 'delivery'
  is_active: boolean
  created_at: string
  store_id: string
}

const StoreEmployees = () => {
  const navigate = useNavigate()
  const { activeStore, stores } = useStore()
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null)
  const [generatedCode, setGeneratedCode] = useState<string | null>(null)
  const [inviteRoles, setInviteRoles] = useState<string[]>(['dispatcher'])
  const [codeCopied, setCodeCopied] = useState(false)
  const [generatingCode, setGeneratingCode] = useState(false)
  const [showReassignModal, setShowReassignModal] = useState(false)
  const [reassignStoreId, setReassignStoreId] = useState<string>('')
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    type: 'dispatcher' as Employee['type']
  })

  // Hook para ocultar/mostrar header en scroll (solo pantallas pequeñas)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isSmallScreen = window.innerWidth < 640

      if (!isSmallScreen) {
        setIsHeaderVisible(true)
        return
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false)
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  useEffect(() => {
    if (activeStore) {
      loadEmployees()
    }
  }, [activeStore])

  const loadEmployees = async () => {
    if (!activeStore) return

    const cacheKey = `store_employees_${activeStore.id}`
    
    // Intentar cargar desde cache primero
    const cachedEmployees = getCacheStale<Employee[]>(cacheKey)
    if (cachedEmployees && cachedEmployees.length > 0) {
      setEmployees(cachedEmployees)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get(`/auth/store/${activeStore.id}/employees`)
      if (response.data.success) {
        const employeesData = response.data.data || []
        setEmployees(employeesData)
        
        // Guardar en cache (10 minutos de TTL - los empleados cambian poco)
        setCache(cacheKey, employeesData, 10 * 60 * 1000)
      } else {
        // Solo borrar si no hay cache
        if (!cachedEmployees || cachedEmployees.length === 0) {
          setEmployees([])
        }
      }
    } catch (error: any) {
      console.error('Error loading employees:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedEmployees) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      // Solo borrar si no hay cache
      if (!cachedEmployees || cachedEmployees.length === 0) {
        setEmployees([])
      }
    } finally {
      setLoading(false)
    }
  }

  const filteredEmployees = employees.filter(employee =>
    employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      type: 'dispatcher'
    })
    setEditingEmployee(null)
  }

  const handleGenerateInviteCode = async () => {
    if (!activeStore) {
      toast.error('No hay un negocio seleccionado')
      return
    }

    if (inviteRoles.length === 0) {
      toast.error('Debes seleccionar al menos un rol')
      return
    }

    try {
      setGeneratingCode(true)
      const response = await api.post('/auth/my-store/invitation-codes', {
        store_id: activeStore.id,
        roles: inviteRoles
      })

      if (response.data.success) {
        setGeneratedCode(response.data.data.code)
        toast.success('Código de invitación generado exitosamente')
      }
    } catch (error: any) {
      console.error('Error generating invite code:', error)
      toast.error(error.response?.data?.message || 'Error al generar código de invitación')
    } finally {
      setGeneratingCode(false)
    }
  }

  const handleCopyCode = () => {
    if (generatedCode) {
      navigator.clipboard.writeText(generatedCode)
      setCodeCopied(true)
      toast.success('Código copiado al portapapeles')
      setTimeout(() => setCodeCopied(false), 2000)
    }
  }

  const handleRoleToggle = (role: string) => {
    if (inviteRoles.includes(role)) {
      if (inviteRoles.length > 1) {
        setInviteRoles(inviteRoles.filter(r => r !== role))
      } else {
        toast.error('Debes seleccionar al menos un rol')
      }
    } else {
      setInviteRoles([...inviteRoles, role])
    }
  }

  const handleAddEmployee = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activeStore) return

    try {
      const response = await api.post('/auth/my-store/employees', {
        email: formData.email.trim().toLowerCase(),
        // No enviar password, el backend generará uno temporal
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        type: formData.type,
        store_id: activeStore.id
      })

      if (response.data.success) {
        const tempPassword = response.data.data?.tempPassword
        if (tempPassword) {
          toast.success(
            `Empleado agregado exitosamente. Contraseña temporal: ${tempPassword}`,
            { duration: 8000 }
          )
          // Opcional: copiar al portapapeles
          navigator.clipboard?.writeText(tempPassword).then(() => {
            toast('Contraseña copiada al portapapeles', { icon: '📋', duration: 3000 })
          })
        } else {
          toast.success('Empleado agregado exitosamente')
        }
        setShowAddModal(false)
        resetForm()
        loadEmployees()
      } else {
        toast.error(response.data.message || 'Error al agregar empleado')
      }
    } catch (error: any) {
      console.error('Error adding employee:', error)
      toast.error(error.response?.data?.message || 'Error al agregar empleado')
    }
  }

  const handleEditEmployee = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingEmployee || !activeStore) return

    try {
      const response = await api.put(`/auth/my-store/employees/${editingEmployee.id}`, {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        type: formData.type
      })

      if (response.data.success) {
        toast.success('Empleado actualizado exitosamente')
        setEditingEmployee(null)
        resetForm()
        setShowAddModal(false)
        loadEmployees()
      } else {
        toast.error(response.data.message || 'Error al actualizar empleado')
      }
    } catch (error: any) {
      console.error('Error updating employee:', error)
      toast.error(error.response?.data?.message || 'Error al actualizar empleado')
    }
  }

  const handleDeleteEmployee = async (employeeId: string) => {
    if (!confirm('¿Estás seguro de que quieres ELIMINAR permanentemente este empleado? Esta acción no se puede deshacer.')) return

    try {
      const response = await api.delete(`/auth/my-store/employees/${employeeId}`)
      if (response.data?.success) {
        toast.success(response.data.message || 'Empleado eliminado exitosamente')
      loadEmployees()
      } else {
        toast.error(response.data?.message || 'Error al eliminar empleado')
      }
    } catch (error: any) {
      console.error('Error deleting employee:', error)
      const errorMessage = error.response?.data?.message || 'Error al eliminar empleado'
      toast.error(errorMessage)
      
      // If employee has active orders, show more helpful message
      if (error.response?.status === 400 && errorMessage.includes('pedido')) {
        toast.error('Primero desactiva al empleado o espera a que complete sus pedidos activos', { duration: 5000 })
      }
    }
  }

  const handleViewDetails = (employee: Employee) => {
    if (!activeStore) {
      toast.error('No hay un negocio seleccionado')
      return
    }
    navigate(`/store/${activeStore.id}/employees/${employee.id}`)
  }

  const handleReassignEmployee = async (employeeId: string) => {
    if (!reassignStoreId) {
      toast.error('Debes seleccionar un negocio')
      return
    }

    // Check if trying to reassign to the same store
    if (selectedEmployee && reassignStoreId === selectedEmployee.store_id) {
      toast.error('El empleado ya está asignado a este negocio')
      return
    }

    try {
      const response = await api.patch(`/auth/my-store/employees/${employeeId}/reassign`, {
        store_id: reassignStoreId
      })
      
      if (response.data.success) {
        toast.success(response.data.message || 'Empleado reasignado exitosamente')
        setShowReassignModal(false)
        setSelectedEmployee(null)
        setReassignStoreId('')
        loadEmployees()
      }
    } catch (error: any) {
      console.error('Error reassigning employee:', error)
      const errorMessage = error.response?.data?.message || 'Error al reasignar empleado'
      toast.error(errorMessage)
      
      // If error is about already assigned, close modal
      if (error.response?.status === 400 && errorMessage.includes('ya está asignado')) {
        setShowReassignModal(false)
        setSelectedEmployee(null)
        setReassignStoreId('')
      }
    }
  }

  const handleToggleStatus = async (employeeId: string, isActive: boolean) => {
    try {
      const response = await api.patch(`/auth/my-store/employees/${employeeId}`, { isActive })
      if (response.data.success) {
        toast.success(isActive ? 'Empleado activado' : 'Empleado desactivado')
        loadEmployees()
      } else {
        toast.error(response.data.message || 'Error al actualizar estado')
      }
    } catch (error: any) {
      console.error('Error updating employee status:', error)
      toast.error(error.response?.data?.message || 'Error al actualizar estado del empleado')
    }
  }

  const openEditModal = (employee: Employee) => {
    setEditingEmployee(employee)
    setFormData({
      name: employee.name,
      email: employee.email,
      phone: employee.phone,
      type: employee.type
    })
  }

  const getRoleIcon = (type: Employee['type']) => {
    switch (type) {
      case 'store_admin': return <User className="w-5 h-5" />
      case 'dispatcher': return <Package className="w-5 h-5" />
      case 'chef': return <ChefHat className="w-5 h-5" />
      case 'delivery': return <Truck className="w-5 h-5" />
      default: return <User className="w-5 h-5" />
    }
  }

  const getRoleColor = (type: Employee['type']) => {
    switch (type) {
      case 'store_admin': return 'bg-purple-100 text-purple-800'
      case 'dispatcher': return 'bg-blue-100 text-blue-800'
      case 'chef': return 'bg-orange-100 text-orange-800'
      case 'delivery': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRoleLabel = (type: Employee['type']) => {
    switch (type) {
      case 'store_admin': return 'Administrador'
      case 'dispatcher': return 'Despachador'
      case 'chef': return 'Cocinero'
      case 'delivery': return 'Repartidor'
      default: return type
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl mb-6">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
          <p className="text-gray-600 font-semibold">Cargando empleados...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 pb-20 sm:pb-8">
      {/* Header - Glassmorphism Style - Ocultable en scroll en pantallas pequeñas */}
      <div className={`bg-white/40 backdrop-blur-2xl border-b border-white/20 shadow-lg sticky top-0 z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="py-3 sm:py-6 lg:py-8">
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-white/60 backdrop-blur-md hover:bg-white/80 border border-white/30 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </button>
              <div className="flex-1 text-center px-2 sm:px-4 min-w-0">
                <h1 className="text-lg sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-1 sm:mb-2 truncate">
                  Gestión de Empleados
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">
                  {employees.length} {employees.length === 1 ? 'empleado' : 'empleados'}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-xl hover:shadow-2xl transition-all duration-300 active:scale-95"
                  title="Generar código de invitación"
                >
                  <Key className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-3 sm:px-5 py-2 sm:py-3 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-semibold transition-all duration-300 active:scale-95 flex items-center gap-1.5 sm:gap-2 bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-xl hover:shadow-2xl"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Nuevo</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12">
        {/* Quick Actions - Compacto */}
        <div className="mb-4 sm:mb-6 lg:mb-8 flex flex-col sm:flex-row gap-2 sm:gap-4">
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl sm:rounded-2xl text-xs sm:text-sm font-semibold transition-all duration-300 active:scale-95 shadow-xl hover:shadow-2xl"
          >
            <Key className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Generar Código de Invitación</span>
            <span className="sm:hidden">Invitar</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl sm:rounded-2xl text-xs sm:text-sm font-semibold transition-all duration-300 active:scale-95 shadow-xl hover:shadow-2xl"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Agregar Empleado</span>
            <span className="sm:hidden">Agregar</span>
          </button>
        </div>

        {/* Search Bar - Compacto */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="relative max-w-2xl mx-auto">
            <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6">
              <div className="relative">
                <Search className="absolute left-3 sm:left-5 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar empleados..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 sm:pl-14 pr-4 sm:pr-5 py-2.5 sm:py-4 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 focus:bg-white/80 transition-all duration-200 text-sm sm:text-base font-semibold text-gray-900 placeholder-gray-400 shadow-lg hover:shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Employees List - Compacto */}
        {filteredEmployees.length === 0 ? (
          <div className="text-center py-8 sm:py-12 lg:py-16">
            <div className="bg-white/70 backdrop-blur-2xl rounded-xl sm:rounded-2xl shadow-2xl border border-white/20 p-6 sm:p-10 lg:p-12 max-w-lg mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl sm:rounded-2xl mb-4 sm:mb-6 lg:mb-8 shadow-xl">
                <User className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-gray-400" />
              </div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-4">No hay empleados registrados</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 font-medium">Agrega tu primer empleado para comenzar a gestionar tu equipo</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl sm:rounded-2xl transition-all duration-300 active:scale-95 text-sm sm:text-base font-semibold shadow-xl hover:shadow-2xl"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Agregar Primer Empleado
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
            {filteredEmployees.map((employee) => (
              <div 
                key={employee.id} 
                className="group bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6 lg:p-8 hover:shadow-xl sm:hover:shadow-2xl hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-500 hover:bg-white/80"
              >
                {/* Employee Header */}
                <div className="flex items-start justify-between mb-4 sm:mb-6">
                  <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <div className="text-white">
                        {React.cloneElement(getRoleIcon(employee.type), { className: 'w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8' })}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mb-1 sm:mb-2 truncate">{employee.name}</h3>
                      <p className="text-xs sm:text-sm lg:text-base text-gray-600 truncate font-medium">{employee.email}</p>
                      {employee.phone && (
                        <p className="text-[10px] sm:text-xs lg:text-sm text-gray-500 truncate font-medium">{employee.phone}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Badges - Compacto */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <span className={`inline-flex items-center px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold border backdrop-blur-sm ${getRoleColor(employee.type)} border-current/20`}>
                    {getRoleLabel(employee.type)}
                  </span>
                  <span className={`inline-flex items-center px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold border backdrop-blur-sm ${
                    employee.is_active
                      ? 'bg-emerald-50/80 text-emerald-700 border-emerald-200/50'
                      : 'bg-red-50/80 text-red-700 border-red-200/50'
                  }`}>
                    {employee.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>

                {/* Action Buttons - Responsive Grid - Compacto */}
                <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
                  <button
                    onClick={() => handleViewDetails(employee)}
                    className="flex flex-col items-center justify-center p-2.5 sm:p-3 lg:p-4 bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold text-purple-700 hover:bg-white/80 transition-all duration-300 active:scale-95 border border-white/30 shadow-lg hover:shadow-xl min-h-[60px] sm:min-h-[70px]"
                    title="Ver detalles"
                  >
                    <Eye className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                    <span className="hidden sm:inline">Detalles</span>
                    <span className="sm:hidden">Ver</span>
                  </button>

                  <button
                    onClick={() => openEditModal(employee)}
                    className="flex flex-col items-center justify-center p-2.5 sm:p-3 lg:p-4 bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold text-blue-700 hover:bg-white/80 transition-all duration-300 active:scale-95 border border-white/30 shadow-lg hover:shadow-xl min-h-[60px] sm:min-h-[70px]"
                    title="Editar"
                  >
                    <Edit className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                    <span className="hidden sm:inline">Editar</span>
                    <span className="sm:hidden">Edit</span>
                  </button>

                  {employee.is_active ? (
                    <button
                      onClick={() => handleToggleStatus(employee.id, false)}
                      className="flex flex-col items-center justify-center p-2.5 sm:p-3 lg:p-4 bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold text-amber-700 hover:bg-white/80 transition-all duration-300 active:scale-95 border border-white/30 shadow-lg hover:shadow-xl min-h-[60px] sm:min-h-[70px]"
                      title="Desactivar"
                    >
                      <UserX className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                      <span className="hidden sm:inline">Desactivar</span>
                      <span className="sm:hidden">Off</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleToggleStatus(employee.id, true)}
                      className="flex flex-col items-center justify-center p-2.5 sm:p-3 lg:p-4 bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold text-emerald-700 hover:bg-white/80 transition-all duration-300 active:scale-95 border border-white/30 shadow-lg hover:shadow-xl min-h-[60px] sm:min-h-[70px]"
                      title="Activar"
                    >
                      <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                      <span className="hidden sm:inline">Activar</span>
                      <span className="sm:hidden">On</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setSelectedEmployee(employee)
                      setShowReassignModal(true)
                    }}
                    className="hidden lg:flex flex-col items-center justify-center p-2.5 sm:p-3 lg:p-4 bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold text-indigo-700 hover:bg-white/80 transition-all duration-300 active:scale-95 border border-white/30 shadow-lg hover:shadow-xl min-h-[60px] sm:min-h-[70px]"
                    title="Reasignar a otro negocio"
                  >
                    <ArrowRightLeft className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                    <span className="hidden lg:inline">Reasignar</span>
                  </button>

                  <button
                    onClick={() => handleDeleteEmployee(employee.id)}
                    className="flex flex-col items-center justify-center p-2 sm:p-2.5 sm:p-3 bg-red-50 rounded-xl text-[10px] sm:text-xs font-medium text-red-700 hover:bg-red-100 transition-all duration-200 active:scale-95 border border-red-100 min-h-[60px] sm:min-h-[70px]"
                    title="Eliminar permanentemente"
                  >
                    <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mb-1" />
                    <span className="hidden sm:inline">Eliminar</span>
                    <span className="sm:hidden">Del</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Employee Modal */}
      {(showAddModal || editingEmployee) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md overflow-y-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-md w-full my-8 border border-gray-200/50">
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-1">
                  {editingEmployee ? 'Editar Empleado' : 'Agregar Empleado'}
                </h2>
                  <p className="text-sm text-gray-500">
                    {editingEmployee ? 'Actualiza la información del empleado' : 'Completa los datos para agregar un nuevo empleado'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false)
                    setEditingEmployee(null)
                    resetForm()
                  }}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {!editingEmployee && (
                <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-4 mb-6 border border-blue-200/50">
                  <p className="text-sm text-blue-800 font-medium">
                    <strong>Nota:</strong> Se generará una contraseña temporal automáticamente. 
                    El empleado deberá cambiarla en su primer inicio de sesión.
                  </p>
                </div>
              )}

              <form onSubmit={editingEmployee ? handleEditEmployee : handleAddEmployee} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                    placeholder="Nombre del empleado"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                    placeholder="correo@ejemplo.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                    placeholder="(809) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Rol *
                  </label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as Employee['type'] })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 shadow-sm hover:shadow-md cursor-pointer"
                  >
                    <option value="dispatcher">Despachador</option>
                    <option value="chef">Cocinero</option>
                    <option value="delivery">Repartidor</option>
                    <option value="store_admin">Administrador</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false)
                      setEditingEmployee(null)
                      resetForm()
                    }}
                    className="flex-1 py-3.5 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 active:scale-95 font-medium border border-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3.5 px-4 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all duration-200 active:scale-95 font-semibold shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30"
                  >
                    {editingEmployee ? 'Actualizar' : 'Agregar'} Empleado
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Invitation Code Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-md w-full my-8 border border-gray-200/50">
            <div className="p-6 sm:p-8 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-1">Generar Código de Invitación</h2>
                  <p className="text-sm text-gray-500">Crea un código para invitar empleados a tu negocio</p>
                </div>
              <button
                onClick={() => {
                  setShowInviteModal(false)
                  setGeneratedCode(null)
                  setInviteRoles(['dispatcher'])
                  setCodeCopied(false)
                }}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95"
              >
                  <X className="w-5 h-5 text-gray-500" />
              </button>
              </div>
            </div>

            <div className="p-6 sm:p-8 space-y-6">
              {!generatedCode ? (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Selecciona los roles para el empleado
                    </label>
                    <div className="space-y-3">
                      {[
                        { value: 'dispatcher', label: 'Despachador', icon: Package },
                        { value: 'chef', label: 'Cocinero', icon: ChefHat },
                        { value: 'delivery', label: 'Repartidor', icon: Truck }
                      ].map(({ value, label, icon: Icon }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => handleRoleToggle(value)}
                          className={`w-full p-4 rounded-2xl border-2 transition-all duration-200 flex items-center gap-3 ${
                            inviteRoles.includes(value)
                              ? 'border-primary-500 bg-primary-50 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                          }`}
                        >
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                            inviteRoles.includes(value) ? 'bg-primary-600' : 'bg-gray-100'
                          }`}>
                            <Icon className={`w-6 h-6 ${inviteRoles.includes(value) ? 'text-white' : 'text-gray-600'}`} />
                          </div>
                          <span className="flex-1 text-left font-semibold text-gray-900">{label}</span>
                          {inviteRoles.includes(value) && (
                            <CheckCircle className="w-6 h-6 text-primary-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100/50 border border-yellow-200 rounded-2xl p-4">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-yellow-900">Vigencia del código</p>
                        <p className="text-xs text-yellow-700 mt-1">
                          El código expirará en 1 minuto. Asegúrate de compartirlo rápidamente con el empleado.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setShowInviteModal(false)
                        setInviteRoles(['dispatcher'])
                        setGeneratedCode(null)
                        setGeneratingCode(false)
                      }}
                      disabled={generatingCode}
                      className="flex-1 px-4 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleGenerateInviteCode}
                      disabled={generatingCode}
                      className="flex-1 px-4 py-3.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30"
                    >
                      {generatingCode ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                          Generando...
                        </>
                      ) : (
                        <>
                          <Key className="w-5 h-5" />
                          Generar Código
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-2">Código Generado</h3>
                    <p className="text-gray-600 mb-6">
                      Comparte este código con el empleado. Expirará en 1 minuto.
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl p-6 border-2 border-dashed border-gray-300">
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Código de Invitación</p>
                      <div className="flex items-center justify-center gap-3 flex-wrap">
                        <code className="text-2xl sm:text-3xl font-mono font-bold text-gray-900 tracking-wider break-all">
                          {generatedCode}
                        </code>
                        <button
                          onClick={handleCopyCode}
                          className="p-2.5 hover:bg-gray-200 rounded-xl transition-all duration-200 active:scale-95"
                          title="Copiar código"
                        >
                          {codeCopied ? (
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          ) : (
                            <Copy className="w-6 h-6 text-gray-600" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 rounded-2xl p-4">
                    <p className="text-sm text-blue-800 font-medium">
                      <strong>Roles asignados:</strong> {inviteRoles.map(r => 
                        r === 'dispatcher' ? 'Despachador' :
                        r === 'chef' ? 'Cocinero' :
                        r === 'delivery' ? 'Repartidor' : r
                      ).join(', ')}
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      setShowInviteModal(false)
                      setGeneratedCode(null)
                      setInviteRoles(['dispatcher'])
                      setCodeCopied(false)
                    }}
                    className="w-full px-4 py-3.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all duration-200 shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30"
                  >
                    Cerrar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reassign Employee Modal */}
      {showReassignModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-md w-full my-8 border border-gray-200/50">
            <div className="p-6 sm:p-8 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-1">Reasignar Empleado</h2>
                  <p className="text-sm text-gray-500">Mueve el empleado a otro negocio</p>
                </div>
              <button
                onClick={() => {
                  setShowReassignModal(false)
                  setSelectedEmployee(null)
                  setReassignStoreId('')
                }}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95"
              >
                  <X className="w-5 h-5 text-gray-500" />
              </button>
              </div>
            </div>

            <div className="p-6 sm:p-8 space-y-5">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Reasignar <strong className="text-gray-900">{selectedEmployee.name}</strong> a otro negocio:
                </p>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Seleccionar Negocio
                </label>
                <select
                  value={reassignStoreId}
                  onChange={(e) => setReassignStoreId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 shadow-sm hover:shadow-md cursor-pointer"
                >
                  <option value="">Selecciona un negocio</option>
                  {stores
                    .filter((store: Store) => store.id !== selectedEmployee.store_id)
                    .map((store: Store) => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                </select>
                {stores.filter((store: Store) => store.id === selectedEmployee.store_id).length > 0 && (
                  <p className="text-xs text-gray-500 mt-2 font-medium">
                    Actualmente asignado a: <strong className="text-gray-700">{stores.find((s: Store) => s.id === selectedEmployee.store_id)?.name}</strong>
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowReassignModal(false)
                    setSelectedEmployee(null)
                    setReassignStoreId('')
                  }}
                  className="flex-1 px-4 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-200 border border-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleReassignEmployee(selectedEmployee.id)}
                  disabled={!reassignStoreId}
                  className="flex-1 px-4 py-3.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30"
                >
                  Reasignar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StoreEmployees
